import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Button,
    ButtonGroup,
    Alert,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
    startOfDay,
    endOfDay,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfYear,
    endOfYear,
    differenceInYears,
    isValid,
    format,
    isWithinInterval,
    eachDayOfInterval,
    eachMonthOfInterval
} from 'date-fns';
import { Search, Assessment, Receipt, TrendingUp, AttachMoney, Payment, CurrencyRupee } from '@mui/icons-material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import FeatureGuard from '../common/FeatureGuard';
import apiService from '../../services/api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { StatCard, Loading } from '../common';

const SalesReport = () => {
    const [filterType, setFilterType] = useState('month');
    const [dateRange, setDateRange] = useState({
        from: startOfMonth(new Date()),
        to: endOfDay(new Date()) // Default to end of today/month for inclusivity
    });
    const [customRange, setCustomRange] = useState({
        from: startOfMonth(new Date()),
        to: endOfDay(new Date())
    });
    const [error, setError] = useState('');
    const [salesData, setSalesData] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false); // Start false, fetch triggering loading
    const { formatPrice } = useCurrency();

    // Initial Fetch
    useEffect(() => {
        fetchInvoices();
    }, []);

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            // Fetch invoices + payments and filter client-side for consistency with other pages
            const [invoicesRes, paymentsRes] = await Promise.all([
                apiService.get('/invoices'),
                apiService.get('/payments'),
            ]);

            if (invoicesRes.success) {
                setSalesData(invoicesRes.data.invoices || []);
            } else {
                setError('Failed to fetch invoice data');
            }

            if (paymentsRes.success) {
                setPayments(paymentsRes.data.payments || []);
            } else {
                setPayments([]);
            }
        } catch (err) {
            setError('Error fetching data: ' + err.message);
        } finally {
            setLoading(false);
        }
    };


    // Handle filter buttons
    const handleFilterChange = (type) => {
        setFilterType(type);
        setError('');

        const now = new Date();
        let newRange = { from: now, to: now };

        switch (type) {
            case 'today':
                newRange = { from: startOfDay(now), to: endOfDay(now) };
                break;
            case 'week':
                newRange = { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfDay(now) };
                break;
            case 'month':
                newRange = { from: startOfMonth(now), to: endOfDay(now) };
                break;
            case 'year':
                newRange = { from: startOfYear(now), to: endOfDay(now) };
                break;
            case 'custom':
                newRange = customRange;
                break;
            default:
                break;
        }

        setDateRange(newRange);
    };

    // Handle custom date changes
    const handleCustomDateChange = (prop, value) => {
        if (!isValid(value)) return;

        // Ensure we capture end of day for 'to' date in custom range for inclusive filtering
        let adjustedValue = value;
        if (prop === 'to') {
            adjustedValue = endOfDay(value);
        } else if (prop === 'from') {
            adjustedValue = startOfDay(value);
        }

        const newCustomRange = { ...customRange, [prop]: adjustedValue };
        setCustomRange(newCustomRange);

        if (newCustomRange.from && newCustomRange.to) {
            if (newCustomRange.from > newCustomRange.to) {
                setError('Start date cannot be after end date');
                return;
            }

            const diff = differenceInYears(newCustomRange.to, newCustomRange.from);
            if (diff >= 1) {
                setError('Date range cannot exceed 1 year');
            } else {
                setError('');
                setDateRange(newCustomRange);
            }
        }
    };

    // Filter Data
    const getFilteredData = () => {
        if (!salesData.length) return [];

        return salesData.filter(invoice => {
            if (!invoice.invoice_date) return false;
            const invoiceDate = new Date(invoice.invoice_date);
            return isWithinInterval(invoiceDate, { start: dateRange.from, end: dateRange.to });
        }).sort((a, b) => new Date(b.invoice_date) - new Date(a.invoice_date));
    };

    const filteredInvoices = getFilteredData();

    // Payments are filtered by payment_date (Revenue Received should reflect actual received amounts)
    const getFilteredPayments = () => {
        if (!payments.length) return [];
        return payments.filter((payment) => {
            if (!payment.payment_date) return false;
            const paymentDate = new Date(payment.payment_date);
            return isWithinInterval(paymentDate, { start: dateRange.from, end: dateRange.to });
        });
    };

    const filteredPayments = getFilteredPayments();

    // Calculate Summaries
    const totalSales = filteredInvoices.reduce((sum, inv) => sum + (parseFloat(inv.total_amount) || 0), 0);
    const totalReceived = filteredPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const totalInvoices = filteredInvoices.length;
    const averageSale = totalInvoices > 0 ? totalSales / totalInvoices : 0;

    // Prepare Chart Data
    const getChartData = () => {
        if (!filteredInvoices.length) return [];

        // Determine grouping based on filter type
        const shouldGroupByDay = filterType === 'week' || filterType === 'month';
        const shouldGroupByMonth = filterType === 'year' || filterType === 'custom';

        if (shouldGroupByDay) {
            // Group by day
            const days = eachDayOfInterval({ start: dateRange.from, end: dateRange.to });
            const dayDataMap = new Map();

            // Initialize all days with 0 revenue
            days.forEach(day => {
                const dayKey = format(day, 'yyyy-MM-dd');
                dayDataMap.set(dayKey, {
                    date: format(day, 'MMM dd'),
                    revenue: 0
                });
            });

            // Aggregate revenue by day
            filteredInvoices.forEach(invoice => {
                if (invoice.invoice_date) {
                    const invoiceDate = new Date(invoice.invoice_date);
                    const dayKey = format(invoiceDate, 'yyyy-MM-dd');
                    if (dayDataMap.has(dayKey)) {
                        const existing = dayDataMap.get(dayKey);
                        existing.revenue += parseFloat(invoice.total_amount) || 0;
                    }
                }
            });

            return Array.from(dayDataMap.values());
        } else if (shouldGroupByMonth) {
            // Group by month
            const months = eachMonthOfInterval({ start: dateRange.from, end: dateRange.to });
            const monthDataMap = new Map();

            // Initialize all months with 0 revenue
            months.forEach(month => {
                const monthKey = format(month, 'yyyy-MM');
                monthDataMap.set(monthKey, {
                    date: format(month, 'MMM yyyy'),
                    revenue: 0
                });
            });

            // Aggregate revenue by month
            filteredInvoices.forEach(invoice => {
                if (invoice.invoice_date) {
                    const invoiceDate = new Date(invoice.invoice_date);
                    const monthKey = format(invoiceDate, 'yyyy-MM');
                    if (monthDataMap.has(monthKey)) {
                        const existing = monthDataMap.get(monthKey);
                        existing.revenue += parseFloat(invoice.total_amount) || 0;
                    }
                }
            });

            return Array.from(monthDataMap.values());
        }

        return [];
    };

    const chartData = getChartData();

    // Prepare Payment Mode Chart Data
    const getPaymentModeChartData = () => {
        if (!filteredPayments.length) return [];

        const paymentModeMap = new Map();

        // Initialize payment modes
        ['cash', 'card', 'upi', 'bank_transfer'].forEach(mode => {
            paymentModeMap.set(mode, 0);
        });

        // Aggregate payment amounts by mode
        filteredPayments.forEach(payment => {
            if (payment.payment_mode && payment.amount) {
                const currentAmount = paymentModeMap.get(payment.payment_mode) || 0;
                paymentModeMap.set(payment.payment_mode, currentAmount + parseFloat(payment.amount));
            }
        });

        // Convert to chart data format with proper labels
        return Array.from(paymentModeMap.entries())
            .filter(([mode, amount]) => amount > 0)
            .map(([mode, amount]) => ({
                mode: mode.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                amount: amount
            }));
    };

    const paymentModeChartData = getPaymentModeChartData();

    if (loading) return <Loading message="Loading sales report..." />;

    return (
        <FeatureGuard feature="Reports & Analytics">
            <Box p={3}>
                {/* Header */}
                <Box display="flex" alignItems="center" mb={3}>
                    <Assessment sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h4" fontWeight="bold">
                            Sales Report
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Overview of sales performance
                        </Typography>
                    </Box>
                </Box>

                {/* Filters Card */}
                <Card sx={{ mb: 4 }}>
                    <CardContent>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={7}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Time Period
                                </Typography>
                                <ButtonGroup variant="outlined" aria-label="time period filters">
                                    <Button
                                        onClick={() => handleFilterChange('today')}
                                        variant={filterType === 'today' ? 'contained' : 'outlined'}
                                    >
                                        Today
                                    </Button>
                                    <Button
                                        onClick={() => handleFilterChange('week')}
                                        variant={filterType === 'week' ? 'contained' : 'outlined'}
                                    >
                                        This Week
                                    </Button>
                                    <Button
                                        onClick={() => handleFilterChange('month')}
                                        variant={filterType === 'month' ? 'contained' : 'outlined'}
                                    >
                                        This Month
                                    </Button>
                                    <Button
                                        onClick={() => handleFilterChange('year')}
                                        variant={filterType === 'year' ? 'contained' : 'outlined'}
                                    >
                                        This Year
                                    </Button>
                                    <Button
                                        onClick={() => handleFilterChange('custom')}
                                        variant={filterType === 'custom' ? 'contained' : 'outlined'}
                                    >
                                        Custom
                                    </Button>
                                </ButtonGroup>
                            </Grid>

                            {filterType === 'custom' && (
                                <Grid item xs={12} md={5}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Custom Range
                                    </Typography>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <Box display="flex" gap={2} alignItems="flex-start">
                                            <DatePicker
                                                label="From"
                                                value={customRange.from}
                                                onChange={(newValue) => handleCustomDateChange('from', newValue)}
                                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                            />
                                            <DatePicker
                                                label="To"
                                                value={customRange.to}
                                                onChange={(newValue) => handleCustomDateChange('to', newValue)}
                                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                            />
                                        </Box>
                                    </LocalizationProvider>
                                </Grid>
                            )}
                        </Grid>

                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                                Reporting Period: <strong>{format(dateRange.from, 'PP')}</strong> - <strong>{format(dateRange.to, 'PP')}</strong>
                            </Typography>
                            <Button variant="outlined" startIcon={<Search />} onClick={fetchInvoices} size="small">
                                Refresh Data
                            </Button>
                        </Box>
                    </CardContent>
                </Card>

                {/* Summary Cards */}
                <Grid container spacing={3} mb={4}>
                    <Grid item xs={12} md={3}>
                        <StatCard
                            title="Total Sales"
                            value={formatPrice(totalSales)}
                            icon={<CurrencyRupee />}
                            color="primary"
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <StatCard
                            title="Revenue Received"
                            value={formatPrice(totalReceived)}
                            icon={<Payment />}
                            color="success"
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <StatCard
                            title="Total Invoices"
                            value={totalInvoices}
                            icon={<Receipt />}
                            color="info"
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <StatCard
                            title="Average Sale"
                            value={formatPrice(averageSale)}
                            icon={<TrendingUp />}
                            color="warning"
                        />
                    </Grid>
                </Grid>

                {/* Sales Chart */}
                {chartData.length > 0 && (
                    <Card sx={{ mb: 4 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Sales Overview
                            </Typography>
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart
                                    data={chartData}
                                    margin={{
                                        top: 20,
                                        right: 30,
                                        left: 20,
                                        bottom: 60,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="date"
                                        angle={-45}
                                        textAnchor="end"
                                        height={100}
                                        interval={0}
                                    />
                                    <YAxis
                                        tickFormatter={(value) => formatPrice(value)}
                                    />
                                    <Tooltip
                                        formatter={(value) => formatPrice(value)}
                                        labelStyle={{ color: '#000' }}
                                    />
                                    <Legend />
                                    <Bar
                                        dataKey="revenue"
                                        fill="#1976d2"
                                        name="Revenue"
                                        radius={[8, 8, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                )}

                {/* Payment Mode Chart */}
                {paymentModeChartData.length > 0 && (
                    <Card sx={{ mb: 4 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Payment Mode Distribution
                            </Typography>
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart
                                    data={paymentModeChartData}
                                    margin={{
                                        top: 20,
                                        right: 30,
                                        left: 20,
                                        bottom: 60,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="mode"
                                        angle={-45}
                                        textAnchor="end"
                                        height={100}
                                        interval={0}
                                    />
                                    <YAxis
                                        tickFormatter={(value) => formatPrice(value)}
                                    />
                                    <Tooltip
                                        formatter={(value) => formatPrice(value)}
                                        labelStyle={{ color: '#000' }}
                                    />
                                    <Legend />
                                    <Bar
                                        dataKey="amount"
                                        fill="#2e7d32"
                                        name="Amount"
                                        radius={[8, 8, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                )}

                {/* Data Table */}
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Invoice #</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredInvoices.length > 0 ? (
                                filteredInvoices.map((invoice) => (
                                    <TableRow key={invoice.id}>
                                        <TableCell>{invoice.invoice_number}</TableCell>
                                        <TableCell>{format(new Date(invoice.invoice_date), 'PP')}</TableCell>
                                        <TableCell sx={{ textTransform: 'capitalize' }}>{invoice.status}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                            {formatPrice(invoice.total_amount)}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        <Box py={3}>
                                            <Typography color="text.secondary">
                                                No invoices found for this period.
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

            </Box>
        </FeatureGuard>
    );
};

export default SalesReport;
